import { useQuery, useQueries, useQueryClient } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaQueryOptions, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaCommPhoneNumberV1Service } from '../service';
import { ResolveIdentitiesByPhoneIO } from '../types';

export const getResolveIdentitiesByPhoneQueryKey = (req: ResolveIdentitiesByPhoneIO['input']) =>
  ['CommPhoneNumberV1', 'ResolveIdentitiesByPhone', req] as const;
type QueryKey = ReturnType<typeof getResolveIdentitiesByPhoneQueryKey>;

/**
 * A hook that returns a query for the `ResolveIdentitiesByPhone` endpoint.
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useResolveIdentitiesByPhoneQuery = <E = unknown, D = ResolveIdentitiesByPhoneIO['output']>(
  req: ResolveIdentitiesByPhoneIO['input'],
  options?: LimitedSchemaQueryOptions<ResolveIdentitiesByPhoneIO, E, D, QueryKey>,
  httpOptions?: Options
) =>
  useQuery<ResolveIdentitiesByPhoneIO['output'], E, D, QueryKey>({
    queryKey: getResolveIdentitiesByPhoneQueryKey(req),
    queryFn: () => SchemaCommPhoneNumberV1Service.ResolveIdentitiesByPhone(req, httpOptions),
    ...options,
  });

/**
 * A hook that returns an array of queries for the `ResolveIdentitiesByPhone` endpoint.
 * It takes an array of objects with the following properties:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useResolveIdentitiesByPhoneQueries = <E = unknown, D = ResolveIdentitiesByPhoneIO['output']>(
  argsArr: {
    req: ResolveIdentitiesByPhoneIO['input'];
    options?: LimitedSchemaQueryOptions<ResolveIdentitiesByPhoneIO, E, D, QueryKey>;
    httpOptions?: Options;
  }[]
) =>
  useQueries(
    argsArr.map(({ req, options, httpOptions }) => ({
      queryKey: getResolveIdentitiesByPhoneQueryKey(req),
      queryFn: () => SchemaCommPhoneNumberV1Service.ResolveIdentitiesByPhone(req, httpOptions),
      ...options,
    }))
  ) as ReturnType<typeof useResolveIdentitiesByPhoneQuery<E, D>>[];

/**
 * A hook that returns a function to fetch the query for the `ResolveIdentitiesByPhone` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchResolveIdentitiesByPhone = () => {
  const queryClient = useQueryClient();

  const fetchResolveIdentitiesByPhone = async <E = unknown, D = ResolveIdentitiesByPhoneIO['output']>(
    req: ResolveIdentitiesByPhoneIO['input'],
    options?: LimitedSchemaFetchQueryOptions<ResolveIdentitiesByPhoneIO, E, D, QueryKey>,
    httpOptions?: Options
  ) => {
    const response = await queryClient.fetchQuery<ResolveIdentitiesByPhoneIO['output'], E, D, QueryKey>(
      getResolveIdentitiesByPhoneQueryKey(req),
      () => SchemaCommPhoneNumberV1Service.ResolveIdentitiesByPhone(req, httpOptions),
      options
    );

    return response;
  };

  return fetchResolveIdentitiesByPhone;
};
